/**
 * Estilos do react-select alinhados ao theme MUI (theme_v2), para dark/light.
 * @param {import('@mui/material/styles').Theme} theme
 * @param {{ menuZIndex?: number, error?: boolean }} [options]
 */
export function getReactSelectStyles(theme, options = {}) {
  const { palette, shape, spacing, typography, zIndex } = theme;
  const menuZ = options.menuZIndex ?? zIndex.modal;
  const hasError = options.error ?? false;

  /** Padding alinhado ao MUI OutlinedInput size="small" (~8.5px 14px). */
  const padY = spacing(0.5);
  const padX = spacing(1.75);

  return {
    control: (base, state) => ({
      ...base,
      cursor: 'default',
      boxSizing: 'border-box',
      fontFamily:
        typography?.fontFamily || 'Roboto, Helvetica, Arial, sans-serif',
      fontSize: typography?.body2?.fontSize ?? 14,
      minHeight: 36,
      minWidth: 0,
      width: '100%',
      alignItems: 'center',
      backgroundColor: palette.background.paper,
      borderColor: hasError
        ? palette.error.main
        : state.isFocused
          ? palette.primary.main
          : palette.divider,
      borderRadius: shape?.borderRadius ?? 8,
      boxShadow: hasError
        ? `0 0 0 1px ${palette.error.main}`
        : state.isFocused
          ? `0 0 0 1px ${palette.primary.main}`
          : 'none',
      paddingLeft: padX,
      paddingRight: spacing(0.5),
    }),
    valueContainer: (base, state) => ({
      ...base,
      paddingTop: padY,
      paddingBottom: padY,
      paddingLeft: 0,
      paddingRight: state.isMulti ? spacing(0.75) : 0,
      margin: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      alignItems: 'center',
      paddingRight: spacing(0.5),
    }),
    menu: (base) => ({
      ...base,
      marginTop: 4,
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      fontSize: 13,
      backgroundColor: palette.background.paper,
      zIndex: menuZ,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: menuZ,
    }),
    menuList: (base) => ({
      ...base,
      backgroundColor: palette.background.paper,
    }),
    option: (base, state) => ({
      ...base,
      cursor: 'default',
      backgroundColor: state.isSelected
        ? palette.primary.main
        : state.isFocused
          ? palette.action.hover
          : 'transparent',
      color: state.isSelected
        ? palette.primary.contrastText
        : palette.text.primary,
    }),
    singleValue: (base) => ({
      ...base,
      color: palette.text.primary,
      marginLeft: 0,
      marginRight: 0,
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor:
        palette.mode === 'dark' ? palette.action.selected : palette.grey[200],
      borderRadius: 16,
      fontSize: '0.75rem',
      margin: '2px 2px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: palette.text.primary,
      fontSize: '0.75rem',
      padding: '1px 4px',
      paddingLeft: 8,
    }),
    multiValueRemove: (base) => ({
      ...base,
      borderRadius: '0 16px 16px 0',
      color: palette.text.secondary,
      padding: '0 6px',
      ':hover': {
        backgroundColor: palette.error.light,
        color: palette.error.main,
      },
    }),
    input: (base) => ({
      ...base,
      color: palette.text.primary,
    }),
    placeholder: (base) => ({
      ...base,
      color: palette.text.secondary,
      margin: 0,
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: palette.divider,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: palette.text.secondary,
      paddingTop: spacing(0.75),
      paddingBottom: spacing(0.75),
      paddingLeft: spacing(0.5),
      paddingRight: spacing(0.75),
    }),
    clearIndicator: (base) => ({
      ...base,
      color: palette.text.secondary,
    }),
    container: (base) => ({
      ...base,
      marginTop: 0,
      minWidth: 0,
      width: '100%',
    }),
  };
}
